import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Warning } from "@phosphor-icons/react";

import { useWorkstationTimeline } from "../../hooks/indigo/useWorkstationApi";
import type { MetricResult, State } from "../../types/indigo";

/**
 * W-IA-2a (IA_CANON_v2 раздел 2.2) — компактная строка risk-чипов на Сегодня.
 *
 * Safety-поверхность: раньше risk-флаги были видны только внутри
 * Анализ → Сигналы (2+ неочевидных тапа, UX_IA_AUDIT 3.9). Здесь — та же
 * выборка metrics state risk/critical, но в одну строку чипов; tap ведёт
 * в Анализ. Пустое состояние — ничего не рендерим (тихо = спокойный сигнал).
 *
 * Данные шарятся с Анализом через тот же react-query ключ
 * (useWorkstationTimeline, staleTime 5 мин) — второго запроса при переходе
 * между табами не будет.
 */

const STATE_RANK: Record<State, number> = {
  critical: 0,
  risk: 1,
  watch: 2,
  optimal: 3,
  info: 4,
};

export function pickRiskChips(
  metrics: MetricResult[],
  max: number,
): MetricResult[] {
  return metrics
    .filter((m) => m.state === "risk" || m.state === "critical")
    .slice()
    .sort((a, b) => {
      const r = STATE_RANK[a.state] - STATE_RANK[b.state];
      if (r !== 0) return r;
      return Math.abs(b.delta_7d ?? 0) - Math.abs(a.delta_7d ?? 0);
    })
    .slice(0, max);
}

export function RiskChipsStrip({ maxChips = 4 }: { maxChips?: number }) {
  const nav = useNavigate();
  const { t } = useTranslation("dashboard");
  const { data } = useWorkstationTimeline();
  const chips = useMemo(
    () => pickRiskChips(data?.metrics ?? [], maxChips),
    [data?.metrics, maxChips],
  );

  if (chips.length === 0) return null;

  return (
    <div
      data-testid="today-risk-chips"
      className="flex items-center gap-1.5 overflow-x-auto"
      role="list"
      aria-label={t("risk_flags.eyebrow", { defaultValue: "RISK FLAGS" })}
    >
      {chips.map((m) => {
        const critical = m.state === "critical";
        return (
          <button
            key={m.name}
            type="button"
            role="listitem"
            data-testid={`today-risk-chip-${m.name}`}
            onClick={() => nav("/analytics")}
            className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-pill text-[11px] font-mono ring-1 ring-inset ${
              critical
                ? "text-bad ring-bad/40 bg-bad/10"
                : "text-warn ring-warn/40 bg-warn/10"
            }`}
          >
            <Warning size={11} aria-hidden />
            <span className="truncate max-w-[140px]">{m.label}</span>
          </button>
        );
      })}
    </div>
  );
}
